import {
  type FC,
  type MouseEventHandler,
  type PropsWithChildren,
  useContext,
} from 'react';
import CheckIcon from '@assets/img/icons/check-2.svg?react';
import { useCompletionContractCheck } from '@common/hooks/completion-contract-check';
import { type ContractDataType } from '@common/types/contract';
import { ContractTimer } from '@components/Home/Contracts/ContractTimer/ContractTimer';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { useCompleteContractMutation } from '@/api/queries/contract';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { playSound } from '@/common/lib/soundEffect';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import { useAppSelector } from '@/redux/store';

import { ContractCard } from '../Contract/ContractCard/ContractCard';
import { ModalContext } from '../Modals/Modal/config';
import { StringXSmall } from '../Typography/Typography';

import GoldenAndCustomItem from './GoldenAndCustomItem';

import css from './styles.module.scss';

import customLockPng from '/img/icons/custom_contract/contract_lock.png';
import customLockWebp from '/img/icons/custom_contract/contract_lock.webp';
import goldenLockPng from '/img/icons/golden_contract/contract_lock.png';
import goldenLockWebp from '/img/icons/golden_contract/contract_lock.webp';
import goldenOpenPng from '/img/icons/golden_contract/contract_open.png';
import goldenOpenWebp from '/img/icons/golden_contract/contract_open.webp';

interface ContractItemProps extends ContractDataType, PropsWithChildren {
  isButton?: boolean;
  inModal?: boolean;
  className?: string;
  isOpenedGolden?: boolean;
}

const ContractItem: FC<ContractItemProps> = ({
  isButton,
  inModal,
  className,
  children,
  isOpenedGolden,
  ...props
}) => {
  const { openModal } = useContext(ModalContext);
  const hasContractCompleted = useCompletionContractCheck(props.data);
  const { data, rejectedAt, point, id, type, isOpened, timeLeft = 0 } = props;
  const nextGoldContractTime = useAppSelector(
    (state) => state.contract.nextGoldContractTime,
  );
  const nextGoldContractSeconds = useCountDownTimer(
    nextGoldContractTime ? nextGoldContractTime / 1000 : 0,
  );
  const seconds = useCountDownTimer(timeLeft < 0 ? 0 : timeLeft / 1000);
  const [, { isUninitialized }] = useCompleteContractMutation({
    fixedCacheKey: 'shared-complete-contract',
  });
  const takeContract: MouseEventHandler<HTMLDivElement> = async (
    event,
  ): Promise<void> => {
    playSound('buttons/proceed');

    if (props.type === 'Golden') {
      openModal({
        renderKind: 'takeContract',
        props: {
          contractData: props,
          seconds,
          nextGoldContractSeconds,
        },
      });
    } else {
      openModal({ renderKind: 'takeContract', props: { contractData: props } });
    }
    event.stopPropagation();
  };

  const pointsCount = Object.keys(data).length;

  const goldenAndCustomData = {
    title: type === 'Golden' ? 'Premium contract' : 'Custom contract',
    type,
    point,
    takeContract,
    isUninitialized,
    isOpened,
    seconds,
    img: {
      png:
        type === 'Golden'
          ? isOpened
            ? goldenOpenPng
            : goldenLockPng
          : customLockPng,
      webp:
        type === 'Golden'
          ? isOpened
            ? goldenOpenWebp
            : goldenLockWebp
          : customLockWebp,
    },
    nextGoldContractSeconds,
    hasContractCompleted,
    inModal,
  };

  const isPrimaryContracts: boolean = ['Golden', 'Custom'].includes(type);

  if (rejectedAt) {
    return (
      <ContractTimer contractType={type} id={id} rejectedAt={rejectedAt} />
    );
  }

  if (isPrimaryContracts && isButton) {
    return <GoldenAndCustomItem {...goldenAndCustomData} />;
  }

  return (
    <div
      onClick={isButton ? takeContract : undefined}
      style={{ display: isPrimaryContracts ? 'flex' : 'grid' }}
      className={cn(
        'h-full w-full aspect-square gap-2 relative',
        isPrimaryContracts
          ? 'grid-rows-1 grid-flow-col'
          : pointsCount >= 3
            ? 'grid-cols-2 grid-rows-2'
            : pointsCount === 2
              ? 'grid-cols-2'
              : '',
        pointsCount === 3 && css.subgrid,
        isButton && 'cursor-pointer',
        className,
      )}
    >
      {children}
      {Object.entries(data).map(([key, value], index) => {
        const isOpen = !isOpenedGolden || type !== 'Golden';

        return (
          <ContractCard
            key={index}
            index={index}
            isOpen={isOpen}
            item={key as ResourceTypeText | PlantTypeText | GoodsType}
            isHorizontal={pointsCount === 3}
            contractType={type}
            inModal={inModal}
            value={value}
          />
        );
      })}
      {point && !inModal && (
        <motion.div
          initial={{ opacity: 0, scale: 1.5, x: '-50%', y: '-50%' }}
          animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
          transition={{ delay: isUninitialized ? 0.6 : 0.8, duration: 0.5 }}
          className={css.level}
        >
          <StringXSmall weight="bold" className="!text-black">
            {point}
          </StringXSmall>
        </motion.div>
      )}
      {hasContractCompleted && !isOpenedGolden && type !== 'Custom' && (
        <div className={css.complete}>
          <div className={css.check}>
            {/* {!isButton ? (
              <CheckIcon className="w-[20px] h-[15px]" />
            ) : (
              <MainSmButton
                className={css.mainButtonText}
                text={t('Show')}
                type="button"
              />
            )} */}
            <CheckIcon className="w-[20px] h-[15px]" />
          </div>
        </div>
      )}
      {!inModal && (
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="relative w-full h-full overflow-hidden rounded-lg">
            <motion.div
              className={cn(
                'absolute top-1/2',
                isUninitialized ? css.gradientLine : 'bg-accent',
              )}
              initial={{
                width: '9.0625rem',
                height: '14.375rem',
                left: '-211%',
                y: '-50%',
                rotate: '30deg',
              }}
              animate={{
                width: '1.25rem',
                height: '11.875rem',
                left: '131.25%',
                y: '-50%',
                rotate: '30deg',
              }}
              transition={{ delay: isUninitialized ? 1.1 : 1.3, duration: 0.6 }}
            ></motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export { ContractItem };
